import { InputForm, Paginator, TableGeneric } from '@components/index'
import { defaultPagination } from '@components/paginator/paginator-utils'
import { IColumn, IListPaginated } from '@interfaces/index'
import { IMaintenances } from '@interfaces/maintenances/IMaintenances'
import { ISearchFilters } from '@interfaces/maintenances/IMaintenancesFilters'
import { Box, Button, Modal } from '@mui/material'
import { errorHandler } from '@services/index'
import MaintenanceService from '@services/requests/maintenanceService'
import { formatDate } from '@utils/dates/dateUtils'
import { phoneMask, totalPages } from '@utils/index'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AiOutlineClose, AiOutlineSearch } from 'react-icons/ai'
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

export default function Maintenances(): React.ReactElement {

    const queryPage = useQuery().get('page')
    const search = useQuery().get('search') as string
    function useQuery(): URLSearchParams {
        return new URLSearchParams(useLocation().search)
    }

    const navigate = useNavigate()
    const [data, setData] = React.useState<IListPaginated<IMaintenances>>(defaultPagination)
    const [isLoading, setIsLoading] = React.useState(false)

    const [currentItem, setCurrentItem] = React.useState<IMaintenances | null>(null)

    const [isOpen, setIsOpen] = React.useState(false)

    const { handleSubmit, control } = useForm<ISearchFilters>({
        defaultValues: {
            search: search ?? ''
        }
    })

    const columns: IColumn<IMaintenances>[] = React.useMemo<IColumn<IMaintenances>[]>(
        () => [
            {
                header: 'Data',
                dataIndex: 'createdAt',
                render: (item) => formatDate(item.createdAt),
            },
            {
                header: 'Cliente',
                dataIndex: 'realEstateApartment',
                render: (item) => (item.realEstateApartment.user.name)
            },
            {
                header: 'Apartamento',
                dataIndex: 'realEstateApartment',
                render: (item) => (item.realEstateApartment.realEstate.name)
            }
        ],
        [data]
    )

    function init(): void {
        if (queryPage)
            getList(parseInt(queryPage), undefined, search)
        else
            getList(1)
    }

    React.useEffect(() => {
        init()
    }, [queryPage, search])

    async function getList(page: number, perPage?: number, search?: string): Promise<void> {
        setIsLoading(true)
        try {
            const response = await MaintenanceService.list(page, perPage, search)
            if (page !== 1 && response.items.length <= 0) {
                navigate(`?page=${page - 1}`)
            } else {
                setData(response)
            }
        } catch (err) {
            toast(errorHandler(err).message, { type: 'error' })
        } finally {
            setIsLoading(false)
        }
    }

    function submit(values: ISearchFilters): void {
        navigate(
            {
                pathname: '/manutencoes',
                search: createSearchParams({
                    page: '1',
                    search: values.search,
                }).toString()
            }
        )
    }

    function handleClose(): void {
        return setIsOpen(false)
    }

    function handleOpen(item: IMaintenances): void {
        setCurrentItem(item)
        return setIsOpen(true)
    }

    return (
        <div className="card">
            <div className="maintenances">
                <div className='page-header'>
                    <h4 className='title'>Solicitações de Manutenção</h4>
                </div>
                <form className='maintenance-filters' onSubmit={handleSubmit(submit)}>
                    <Controller
                        control={control}
                        name='search'
                        render={({ field: { value, onChange } }) => (
                            <InputForm
                                size="small"
                                value={value}
                                onChange={(e) => { onChange(e) }}
                                onInputCallBack={() => {
                                    handleSubmit(submit)()
                                }}
                                placeholder="Buscar por nome ou apartamento"
                                style={{ width: '23rem', marginRight: '1rem' }}
                                InputProps={{
                                    endAdornment: (
                                        <button type='submit' className='button-search'>
                                            <AiOutlineSearch />
                                        </button>
                                    )
                                }}
                            />
                        )}
                    />
                </form>
                <div>
                    <TableGeneric data={data.items} columns={columns} onLineClick={handleOpen} isLoading={isLoading} />
                    <div className="paginator">
                        <Paginator
                            maxElements={7}
                            current={data.page}
                            total={totalPages(data.count, data.perPage)}
                            onClick={(page) => navigate(
                                {
                                    pathname: '/manutencoes',
                                    search: createSearchParams({
                                        page: page.toString(),
                                        search: search ? search : ''
                                    }).toString()
                                }
                            )} />
                    </div>
                </div>
                <Modal
                    open={isOpen}
                    onClose={handleClose}
                    aria-labelledby="modal-modal-title"
                >
                    <Box>
                        <div className="container-modal">
                            <Button onClick={() => setIsOpen(false)} className='btnClose'>
                                <AiOutlineClose />
                            </Button>
                            <div className="wrapper-header">
                                <h4 id="parent-modal-title">Detalhes da Solicitação</h4>
                            </div>
                            <div className="wrapper-infos">
                                <div className="infos-1">
                                    <div className="info-cliente">
                                        <h5>Cliente</h5>
                                        <p>{currentItem && currentItem.realEstateApartment.user.name}</p>
                                    </div>
                                    <div className="info-data">
                                        <h5>Data</h5>
                                        <p>{currentItem && formatDate(currentItem.createdAt)}</p>
                                    </div>
                                </div>
                                <div className="infos-2">
                                    <div className="info-email">
                                        <h5>E-mail</h5>
                                        <p>{currentItem && currentItem.email}</p>
                                    </div>
                                    <div className="info-telefone">
                                        <h5>Telefone</h5>
                                        <p>{currentItem && phoneMask(currentItem.phone)}</p>
                                    </div>
                                </div>
                                <div className="description">
                                    <h5>Descrição da Solicitação</h5>
                                    <p>{currentItem && currentItem.request}</p>
                                </div>
                            </div>
                        </div>
                    </Box>
                </Modal>
            </div>
        </div>
    )
}
